import { FC, useCallback, useMemo, useState } from 'react';
import {
  RiskProfile,
  RiskProfileSignal,
  RiskProfileUpdateSignalsRequest,
} from '@/api';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  INFLUENCES_TO_DISPLAY,
  RISK_GROUPS,
  RISK_KEY_SUBSTITUTIONS,
  RiskProfileInfluenceEnum,
  SIGNAL_INFLUENCES,
} from '@/config';
import { SignalTableRow } from '@/redux/models';
import { useAppDispatch } from '@/redux/hooks';
import { riskscoreProfilesActions } from '@/redux/actions';
import { Table } from 'antd';
import { AlertBadge } from '@/components/AlertBadge';
import { SignalBadge } from '@/components/SignalBadge';
import { SvgIcon } from '@/components/SvgIcon';
import { SignalExpandedRow } from './components/SignalExpandedRow';

import styles from './RiskProfileSignalTable.module.sass';

interface ExpandIconProps {
  expanded: boolean;
  onExpand: (
    record: SignalTableRow,
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => void;
  record: SignalTableRow;
}

interface RiskProfileSignalTableProps {
  profile: RiskProfile;
  loading: boolean;
}

const RiskProfileSignalTable: FC<RiskProfileSignalTableProps> = (props) => {
  const { profile, loading } = props;
  const { t } = useTranslation();
  const [tableKey, setTableKey] = useState(0);
  const [expandedRowKey, setExpandedRowKey] = useState<React.Key>('');
  const dispatch = useAppDispatch();

  const handleExpandChange = useCallback(
    (keys: readonly React.Key[]) => {
      const newKey = keys.length > 0 ? keys[keys.length - 1] : '';
      setExpandedRowKey(newKey === expandedRowKey ? '' : newKey);
    },
    [expandedRowKey],
  );

  const signalData = useMemo(() => {
    if (!profile?.signals) {
      return [];
    }

    const riskCategories = [
      ...RISK_GROUPS.trusted,
      ...RISK_GROUPS.suspicious,
      ...RISK_GROUPS.danger,
    ];

    const rows: SignalTableRow[] = [];

    riskCategories.forEach((category) => {
      const rawSignal = profile.signals?.find(
        (item) =>
          item.signal === category ||
          RISK_KEY_SUBSTITUTIONS[item.signal] === category,
      );

      if (rawSignal) {
        const tableSignal: SignalTableRow = {
          key: rawSignal.signal,
          label: RISK_KEY_SUBSTITUTIONS[rawSignal.signal] || rawSignal.signal,
          received: rawSignal.recv,
          sent: rawSignal.sent,
          riskscore: Math.round(rawSignal.riskscore * 100),
        };

        rows.push(tableSignal);
      }
    });

    return rows;
  }, [profile?.signals]);

  const renderExpandIcon = ({
    expanded,
    onExpand,
    record,
  }: ExpandIconProps) => {
    return (
      <button
        type="button"
        className={cx(styles.edit, {
          [styles['is-active']]: expanded,
        })}
        onClick={(e) => onExpand(record, e)}
      >
        <SvgIcon name="pencil" />
      </button>
    );
  };

  const onSave = useCallback(
    async (value: RiskProfileSignal) => {
      const payload: RiskProfileUpdateSignalsRequest = {
        profileId: profile.id,
        signals: [value],
      };

      dispatch(riskscoreProfilesActions.setProfileSignals(payload));
      setExpandedRowKey('');
    },
    [profile.id],
  );

  const renderExpandRow = useCallback(
    (record: SignalTableRow) => {
      return <SignalExpandedRow signal={record} submitHandler={onSave} />;
    },
    [onSave],
  );

  const { Column } = Table;

  return (
    <div className={styles.table}>
      <Table
        key={tableKey}
        dataSource={signalData}
        size="small"
        pagination={false}
        className={`${styles.table} ${loading ? styles.table_blur : ''}`}
        expandable={
          !profile.locked
            ? {
                columnWidth: '5%',
                expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
                expandIcon: (iconProps) => renderExpandIcon(iconProps),
                expandedRowRender: (record) => renderExpandRow(record),
                expandedRowClassName: () => styles.expandedRow,
                onExpandedRowsChange: (keys) => handleExpandChange(keys),
              }
            : {}
        }
        bordered
        scroll={{ x: 'max-content', y: 'var(--risk-table-scroll-height)' }}
      >
        <Column
          title={t('translation.risk_col_category')}
          key="category"
          width="30%"
          dataIndex="label"
          render={(value) => <SignalBadge value={value} />}
        />
        <Column
          title={t('translation.risk_col_riskscore')}
          key="riskscore"
          dataIndex="riskscore"
          width="20%"
          align="center"
        />
        <Column
          title={t('translation.risk_col_received')}
          key="received"
          dataIndex="received"
          width="20%"
          align="center"
          render={(value) => (
            <AlertBadge
              type={INFLUENCES_TO_DISPLAY[value as RiskProfileInfluenceEnum]}
              label={SIGNAL_INFLUENCES[value as RiskProfileInfluenceEnum]}
            />
          )}
        />
        <Column
          title={t('translation.risk_col_sent')}
          key="sent"
          dataIndex="sent"
          width="20%"
          align="center"
          render={(value) => (
            <AlertBadge
              type={INFLUENCES_TO_DISPLAY[value as RiskProfileInfluenceEnum]}
              label={SIGNAL_INFLUENCES[value as RiskProfileInfluenceEnum]}
            />
          )}
        />
      </Table>
    </div>
  );
};

export default RiskProfileSignalTable;
